import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { getProductFreight, updateProductFreight } from '../../../felles/APICalls_Freight';
import { ADR_CLASSES, COUNTRY_OPTIONS, CountryOption } from '../../../felles/FreightConstants';

/** Verdier som skrives tilbake til de skjulte VAVE-skjermfeltene etter lagring. */
export interface FreightScreenValues {
  LENGDE: string;
  BREDDE: string;
  HOYDE: string;
  VEKT: string;
}

interface Props {
  /** EDBNR lest fra VAVE-skjermen (SEDBNR); tom streng når ingen artikkel er valgt */
  edbNumber: string;
  /** true i spørremodus — alle felter låst og lagring skjult */
  readOnly: boolean;
  onApplyScreenFields: (values: FreightScreenValues) => void;
  onClose: () => void;
}

/** '12,5' | '12.5' → 12.5; tom/ugyldig → null */
const parseWeight = (value: string): number | null => {
  const trimmed = value.trim().replace(',', '.');
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : NaN as unknown as number;
};

const parseDim = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  return /^\d+$/.test(trimmed) ? parseInt(trimmed, 10) : (NaN as unknown as number);
};

/** 12.5 → '12,50' for VEKT-skjermfeltet (ZZZ,ZZ); null/0 → '0' */
const formatWeightForScreen = (value: number | null): string =>
  value ? value.toFixed(2).replace('.', ',') : '0';

export default function FreightDialog(props: Props) {
  const { edbNumber, readOnly, onApplyScreenFields, onClose } = props;
  const parsedEdbNumber = /^\d+$/.test(edbNumber.trim()) ? parseInt(edbNumber.trim(), 10) : null;

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [infoMsg, setInfoMsg] = useState<string | null>(null);

  const [lengde, setLengde] = useState('');
  const [bredde, setBredde] = useState('');
  const [hoyde, setHoyde] = useState('');
  const [nettovekt, setNettovekt] = useState('');
  const [bruttovekt, setBruttovekt] = useState('');
  const [land, setLand] = useState<CountryOption | null>(null);
  const [unNummer, setUnNummer] = useState('');
  const [fareklasse, setFareklasse] = useState('');
  const [hsNummer, setHsNummer] = useState('');

  useEffect(() => {
    if (parsedEdbNumber === null || parsedEdbNumber === 0) {
      setIsLoading(false);
      setInfoMsg('Ingen artikkel er valgt i VAVE.');
      return;
    }
    getProductFreight(parsedEdbNumber)
      .then((response) => {
        const dto = response.data;
        if (!dto.found) {
          setInfoMsg(dto.message || 'Artikkel ikke funnet');
          return;
        }
        setLengde(dto.length != null ? String(dto.length) : '');
        setBredde(dto.width != null ? String(dto.width) : '');
        setHoyde(dto.height != null ? String(dto.height) : '');
        setNettovekt(dto.netWeight != null ? String(dto.netWeight).replace('.', ',') : '');
        setBruttovekt(dto.grossWeight != null ? String(dto.grossWeight).replace('.', ',') : '');
        setLand(COUNTRY_OPTIONS.find((o) => o.alpha3 === dto.countryOfOrigin?.toUpperCase()) ?? null);
        setUnNummer(dto.unNumber ?? '');
        setFareklasse(dto.dangerClass ?? '');
        setHsNummer(dto.hsNumber ?? '');
      })
      .catch(() => setErrorMsg('Kunne ikke hente fraktinformasjon. Prøv igjen.'))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = (): string | null => {
    for (const [label, value] of [['Lengde', lengde], ['Bredde', bredde], ['Høyde', hoyde]] as const) {
      const parsed = parseDim(value);
      if (parsed !== null && (Number.isNaN(parsed) || parsed > 9999)) {
        return `${label} må være et heltall mellom 0 og 9999 mm`;
      }
    }
    for (const [label, value] of [['Nettovekt', nettovekt], ['Bruttovekt', bruttovekt]] as const) {
      const parsed = parseWeight(value);
      if (parsed !== null && (Number.isNaN(parsed) || parsed < 0 || parsed > 999.99)) {
        return `${label} må være mellom 0 og 999,99 kg`;
      }
    }
    if (unNummer.trim() !== '' && !/^\d{4}$/.test(unNummer.trim())) {
      return 'UN-nummer må bestå av nøyaktig 4 siffer';
    }
    if (hsNummer.trim() !== '' && !/^[0-9A-Za-z]{1,15}$/.test(hsNummer.trim())) {
      return 'HS-nummer kan bestå av inntil 15 tegn (bokstaver og siffer)';
    }
    return null;
  };

  const handleSave = () => {
    if (parsedEdbNumber === null || parsedEdbNumber === 0) return;
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }
    setErrorMsg(null);
    setIsSaving(true);
    updateProductFreight({
      edbNumber: parsedEdbNumber,
      length: parseDim(lengde),
      width: parseDim(bredde),
      height: parseDim(hoyde),
      netWeight: parseWeight(nettovekt),
      grossWeight: parseWeight(bruttovekt),
      countryOfOrigin: land?.alpha3 ?? null,
      unNumber: unNummer.trim() || null,
      dangerClass: fareklasse || null,
      hsNumber: hsNummer.trim() || null,
    })
      .then((response) => {
        if (response.data.updated) {
          // Synk de skjulte skjermfeltene slik at neste VAVE-lagring (ENTER) poster de
          // nye verdiene — COBOL skriver skjermverdiene over recorden ved hver lagring.
          onApplyScreenFields({
            LENGDE: String(parseDim(lengde) ?? 0),
            BREDDE: String(parseDim(bredde) ?? 0),
            HOYDE: String(parseDim(hoyde) ?? 0),
            VEKT: formatWeightForScreen(parseWeight(nettovekt)),
          });
          onClose();
        } else {
          setErrorMsg(response.data.message || 'Lagring feilet');
        }
      })
      .catch(() => setErrorMsg('Lagring feilet. Prøv igjen.'))
      .finally(() => setIsSaving(false));
  };

  const fieldsDisabled = readOnly || isLoading || isSaving || infoMsg !== null;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth sx={{ zIndex: 2600 }}>
      <DialogTitle>Fraktinformasjon</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {infoMsg ? <Alert severity="info">{infoMsg}</Alert> : null}
            {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
            <Stack direction="row" spacing={2}>
              <TextField label="Lengde (mm)" value={lengde} onChange={(e) => setLengde(e.target.value)}
                disabled={fieldsDisabled} size="small" slotProps={{ htmlInput: { maxLength: 4, inputMode: 'numeric' } }} />
              <TextField label="Bredde (mm)" value={bredde} onChange={(e) => setBredde(e.target.value)}
                disabled={fieldsDisabled} size="small" slotProps={{ htmlInput: { maxLength: 4, inputMode: 'numeric' } }} />
              <TextField label="Høyde (mm)" value={hoyde} onChange={(e) => setHoyde(e.target.value)}
                disabled={fieldsDisabled} size="small" slotProps={{ htmlInput: { maxLength: 4, inputMode: 'numeric' } }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Nettovekt (kg)" value={nettovekt} onChange={(e) => setNettovekt(e.target.value)}
                disabled={fieldsDisabled} size="small" slotProps={{ htmlInput: { maxLength: 6, inputMode: 'decimal' } }} />
              <TextField label="Bruttovekt (kg)" value={bruttovekt} onChange={(e) => setBruttovekt(e.target.value)}
                disabled={fieldsDisabled} size="small" slotProps={{ htmlInput: { maxLength: 6, inputMode: 'decimal' } }} />
            </Stack>
            <Autocomplete
              options={COUNTRY_OPTIONS}
              groupBy={(option) => option.group}
              getOptionLabel={(option) => `${option.label} (${option.alpha3})`}
              isOptionEqualToValue={(option, value) => option.alpha3 === value.alpha3}
              value={land}
              onChange={(_e, value) => setLand(value)}
              disabled={fieldsDisabled}
              size="small"
              renderInput={(params) => <TextField {...params} label="Opprinnelsesland" />}
              slotProps={{ popper: { sx: { zIndex: 2700 } } }}
            />
            <Stack direction="row" spacing={2}>
              <TextField label="UN-nummer" value={unNummer} onChange={(e) => setUnNummer(e.target.value)}
                disabled={fieldsDisabled} size="small" sx={{ flex: 1 }}
                slotProps={{ htmlInput: { maxLength: 4, inputMode: 'numeric' } }} />
              <TextField label="Fareklasse" value={fareklasse} onChange={(e) => setFareklasse(e.target.value)}
                disabled={fieldsDisabled} size="small" sx={{ flex: 1 }} select
                slotProps={{ select: { MenuProps: { sx: { zIndex: 2700 } } } }}>
                <MenuItem value="">
                  <em>Ingen</em>
                </MenuItem>
                {ADR_CLASSES.map((adrClass) => (
                  <MenuItem key={adrClass} value={adrClass}>{adrClass}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField label="HS-nummer (tolltariff)" value={hsNummer} onChange={(e) => setHsNummer(e.target.value)}
              disabled={fieldsDisabled} size="small"
              slotProps={{ htmlInput: { maxLength: 15 } }} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Avbryt</Button>
        {!readOnly && infoMsg === null ? (
          <Button onClick={handleSave} variant="contained" disabled={isLoading || isSaving}>
            {isSaving ? 'Lagrer…' : 'Lagre'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
